import { useState, useCallback, useRef } from 'react';
import ENDPOINTS from '../api/endPoints';
import type {
  UseTearSheetOptions,
  UseTearSheetReturn,
  TearSheetStatus,
  TearSheetApiResponse,
} from '../types/tearsheet.types';

async function callTearSheetApi(file: File, dimensions: string): Promise<string> {
  const formData = new FormData();
  formData.append('furniture', file);
  const encodedDimensions = encodeURIComponent(dimensions.trim());
  const url = `${ENDPOINTS.furnitureWithDimensions}?dimensions=${encodedDimensions}`;

  const response = await fetch(url, { method: 'POST', body: formData });
  if (!response.ok) {
    throw new Error(`Generation failed: ${response.status} ${response.statusText}`);
  }

  const data: TearSheetApiResponse = await response.json();
  if (!data.image_url) {
    throw new Error(data.error || 'No image URL in response');
  }

  return data.image_url;
}

async function urlToFile(imageUrl: string, filename = 'furniture.jpg'): Promise<File> {
  const res = await fetch(imageUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/jpeg' });
}

export function useTearSheet(options: UseTearSheetOptions = {}): UseTearSheetReturn {
  const { defaultDimensions = '', onGenerated } = options;

  const [dimensions, setDimensionsRaw] = useState(defaultDimensions);
  const [dimensionsEdited, setDimensionsEdited] = useState(false);
  const [status, setStatus] = useState<TearSheetStatus>('idle');
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defaultDimsRef = useRef(defaultDimensions);
  defaultDimsRef.current = defaultDimensions;

  const onGeneratedRef = useRef(onGenerated);
  onGeneratedRef.current = onGenerated;

  const isGenerating = status === 'generating';

  const setDimensions = useCallback((value: string) => {
    setDimensionsRaw(value);
    setDimensionsEdited(value.trim() !== defaultDimsRef.current);
    setError(null);
  }, []);

  const executeGeneration = useCallback(async (file: File) => {
    if (!dimensions.trim() || isGenerating) return;

    setStatus('generating');
    setError(null);

    try {
      const imageUrl = await callTearSheetApi(file, dimensions);
      setGeneratedImageUrl(imageUrl);
      setStatus('success');
      setDimensionsEdited(false);
      onGeneratedRef.current?.(imageUrl);
    } catch (err: any) {
      setError(err.message || 'Failed to generate tear sheet');
      setStatus('error');
    }
  }, [dimensions, isGenerating]);

  const generateFromFile = useCallback(async (file: File) => {
    await executeGeneration(file);
  }, [executeGeneration]);

  const generateFromUrl = useCallback(async (imageUrl: string) => {
    try {
      setStatus('generating');
      setError(null);
      const file = await urlToFile(imageUrl);
      await executeGeneration(file);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch source image');
      setStatus('error');
    }
  }, [executeGeneration]);

  const reset = useCallback((newDefaultDimensions?: string) => {
    const dims = newDefaultDimensions ?? defaultDimsRef.current;
    defaultDimsRef.current = dims;
    setDimensionsRaw(dims);
    setDimensionsEdited(false);
    setStatus('idle');
    setGeneratedImageUrl(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    if (status === 'error') setStatus('idle');
  }, [status]);

  return {
    dimensions,
    dimensionsEdited,
    status,
    isGenerating,
    generatedImageUrl,
    error,
    setDimensions,
    generateFromFile,
    generateFromUrl,
    reset,
    clearError,
  };
}

export default useTearSheet;
